import { Target, Sun, Users, Zap, Shield, Leaf } from 'lucide-react';
import { Footer } from '../components/Footer';
import { HomeHeader } from '../components/HomeHeader';

export function AboutUs() {
  return (
    <div className="min-h-screen bg-white">
      <HomeHeader />
      
      <main>
        {/* Hero Section */}
        <div className="relative bg-gradient-to-r from-blue-600 to-green-600 text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                About ENTEK.AI
              </h1>
              <p className="text-xl text-blue-100 max-w-3xl mx-auto">
                Leading the renewable energy revolution with innovative AI-powered energy solutions 
                and a commitment to sustainability.
              </p>
            </div>
          </div>
        </div>

        {/* Mission & Vision */}
        <div className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              <div className="bg-blue-50 rounded-xl p-8 shadow-md">
                <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mb-6">
                  <Target className="h-6 w-6 text-blue-600" />
                </div>
                <h2 className="text-2xl font-bold mb-4">Our Mission</h2>
                <p className="text-gray-700 leading-relaxed">
                  To accelerate the world's transition to sustainable energy by making intelligent energy solutions 
                  accessible, affordable, and simple for everyone. We're committed to providing 
                  AI-powered design solutions that help our customers reduce their carbon footprint 
                  while maximizing efficiency and savings.
                </p>
              </div>

              <div className="bg-green-50 rounded-xl p-8 shadow-md">
                <div className="bg-green-100 w-12 h-12 rounded-full flex items-center justify-center mb-6">
                  <Sun className="h-6 w-6 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold mb-4">Our Vision</h2>
                <p className="text-gray-700 leading-relaxed">
                  To be the leading provider of AI-powered energy design solutions, recognized globally for our 
                  innovation, reliability, and commitment to sustainability. We envision a future 
                  where clean, renewable energy powers homes and businesses worldwide, optimized by 
                  intelligent systems.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Key Statistics */}
        <div className="bg-gray-50 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Our Impact</h2>
              <p className="text-gray-600 max-w-3xl mx-auto">
                Making a difference in communities through intelligent energy solutions.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="bg-white rounded-xl p-6 text-center shadow-md border border-gray-100">
                <div className="text-4xl font-bold text-blue-600 mb-2">1000+</div>
                <div className="text-gray-600">Projects Designed</div>
              </div>
              <div className="bg-white rounded-xl p-6 text-center shadow-md border border-gray-100">
                <div className="text-4xl font-bold text-green-600 mb-2">50K+</div>
                <div className="text-gray-600">CO₂ Tons Saved</div>
              </div>
              <div className="bg-white rounded-xl p-6 text-center shadow-md border border-gray-100">
                <div className="text-4xl font-bold text-purple-600 mb-2">95%</div>
                <div className="text-gray-600">Customer Satisfaction</div>
              </div>
              <div className="bg-white rounded-xl p-6 text-center shadow-md border border-gray-100">
                <div className="text-4xl font-bold text-amber-600 mb-2">25+</div>
                <div className="text-gray-600">Years Experience</div>
              </div>
            </div>
          </div>
        </div>

        {/* Core Values */}
        <div className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Our Core Values</h2>
              <p className="text-gray-600 max-w-3xl mx-auto">
                The principles that guide our work and relationships.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="bg-blue-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Integrity</h3>
                <p className="text-gray-600">
                  We conduct business with honesty and transparency.
                </p>
              </div>

              <div className="text-center">
                <div className="bg-green-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Leaf className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Sustainability</h3>
                <p className="text-gray-600">
                  We're committed to environmental stewardship.
                </p>
              </div>

              <div className="text-center">
                <div className="bg-purple-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Zap className="h-8 w-8 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Innovation</h3>
                <p className="text-gray-600">
                  We continuously improve our solutions with AI and technology.
                </p>
              </div>

              <div className="text-center">
                <div className="bg-amber-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="h-8 w-8 text-amber-600" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Customer Focus</h3>
                <p className="text-gray-600">
                  We put our customers' needs first.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-blue-600 to-green-600 text-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold mb-6">Ready to Make a Difference?</h2>
            <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
              Join us in creating a sustainable future with intelligent, AI-optimized energy solutions.
            </p>
            <button className="bg-white text-blue-600 py-3 px-8 rounded-lg hover:bg-blue-50 font-medium transition-colors">
              Start Your Journey
            </button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}